// Persistent, per-variant record of completed daily deals, kept in localStorage.
// A "daily" is a deal seeded purely from a calendar date, so it's reproducible:
// everyone playing a given date gets the same shuffle, and a win is remembered
// forever (or until the browser store is cleared).

const KEY = 'cardtoys:daily:v1';

type Store = Record<string, string[]>; // variant -> ["YYYY-MM-DD", ...]

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function write(store: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // Private mode / quota / disabled storage - the record is a nicety, so
    // fail silently rather than break the game.
  }
}

export function getCompleted(variant: string): Set<string> {
  return new Set(read()[variant] ?? []);
}

export function markCompleted(variant: string, date: string): void {
  const store = read();
  const list = store[variant] ?? (store[variant] = []);
  if (!list.includes(date)) {
    list.push(date);
    write(store);
  }
}

// --- date helpers (local time, formatted YYYY-MM-DD) ---

export function dateToStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayStr(): string {
  return dateToStr(new Date());
}

// Seed derived purely from the calendar date. Matches the original dailySeed()
// (year*10000 + month*100 + day) so existing daily deals stay stable.
export function seedForDateStr(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return y! * 10000 + m! * 100 + d!;
}
